<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { ArrowLeft, Connection, Key, Lock, Message, Right, Setting, SwitchButton } from "@element-plus/icons-vue";
import { useSessionStore } from "./stores/session";
import ControlConsole from "./components/ControlConsole.vue";
import Mailbox from "./components/Mailbox.vue";

const session = useSessionStore();
const clock = ref("");
const loginEmail = ref("");
const loginCode = ref("");
const codeInput = ref<HTMLInputElement | null>(null);
const activeBrand = computed(() => session.bootstrap ?? session.authContext);
const productMark = computed(() => activeBrand.value?.productName?.slice(0, 1).toUpperCase() || "M");

async function requestCode(): Promise<void> {
  try {
    await session.requestCode(loginEmail.value);
    loginCode.value = "";
    await nextTick();
    codeInput.value?.focus();
  } catch { /* Store exposes a user-facing error. */ }
}

async function verifyCode(): Promise<void> {
  try { await session.verifyCode(loginCode.value); }
  catch { /* Store exposes a user-facing error. */ }
}

function changeEmail(): void {
  loginEmail.value = session.challengeEmail;
  loginCode.value = "";
  session.resetChallenge();
}

async function resendCode(): Promise<void> {
  loginEmail.value = session.challengeEmail;
  await requestCode();
}

async function logout(): Promise<void> {
  try { await session.logout(); }
  catch { /* A failed logout keeps the current authenticated view available. */ }
}

onMounted(() => {
  void session.load();
  const renderClock = () => { clock.value = new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()); };
  renderClock();
  window.setInterval(renderClock, 60_000);
});
</script>

<template>
  <div v-if="session.loading" class="boot-screen">
    <div class="seal">MC</div>
    <p>正在校验安全会话</p>
    <span class="loading-rule" />
  </div>

  <div v-else-if="!session.bootstrap" class="auth-screen">
    <div class="auth-shell">
      <aside class="auth-aside">
        <div class="auth-brand">
          <span class="brand-seal">{{ productMark }}</span>
          <div>
            <strong>{{ activeBrand?.productName || 'Mail Center' }}</strong>
            <span>{{ activeBrand?.mode === 'mail' ? activeBrand.domain : 'CONTROL OFFICE' }}</span>
          </div>
        </div>
        <div class="auth-statement">
          <span class="eyebrow">PRIVATE CORRESPONDENCE</span>
          <h2>你的邮件，<br>只向你打开。</h2>
          <p>一次性验证码由独立邮件通道送达。我们不保存密码，也不会把身份交给第三方登录页。</p>
        </div>
        <div class="auth-ledger">
          <span>01 · 邮箱授权</span>
          <span>02 · 单次验证码</span>
          <span>03 · 8 小时安全会话</span>
        </div>
      </aside>

      <section class="auth-card paper-card">
        <template v-if="session.error || !session.authContext">
          <div class="eyebrow"><Lock /> SECURITY DESK</div>
          <h1>登录服务暂不可用</h1>
          <p>{{ session.error || '无法读取当前邮箱域的登录配置。' }}</p>
          <button class="primary-button auth-submit" type="button" @click="session.load">重新连接</button>
        </template>

        <template v-else-if="!session.challengeId">
          <div class="auth-step"><span>STEP 1</span><i /><em>2</em></div>
          <div class="eyebrow"><Lock /> VERIFIED EMAIL</div>
          <h1>{{ session.authContext.mode === 'control' ? '进入中央控制台' : '打开你的邮箱' }}</h1>
          <p>输入已获授权的登录邮箱，我们会发送一枚 6 位一次性验证码。</p>
          <form class="auth-form" @submit.prevent="requestCode">
            <label for="login-email">登录邮箱</label>
            <div class="auth-input-wrap">
              <Message />
              <input id="login-email" v-model.trim="loginEmail" type="email" inputmode="email" autocomplete="email" placeholder="name@example.com" required maxlength="254">
            </div>
            <p v-if="session.authError" class="auth-error" role="alert">{{ session.authError }}</p>
            <button class="primary-button auth-submit" type="submit" :disabled="session.submitting || !loginEmail">
              {{ session.submitting ? '正在发送…' : '发送登录验证码' }} <Right />
            </button>
          </form>
          <small>为保护账号隐私，无论邮箱是否存在，系统都会显示相同的受理结果。</small>
        </template>

        <template v-else>
          <div class="auth-step"><button type="button" aria-label="返回修改邮箱" @click="changeEmail"><ArrowLeft /></button><i class="complete" /><span>STEP 2</span></div>
          <div class="eyebrow"><Key /> ONE-TIME CODE</div>
          <h1>查收安全验证码</h1>
          <p>{{ session.authNotice }}<br><strong>{{ session.challengeEmail }}</strong></p>
          <form class="auth-form" @submit.prevent="verifyCode">
            <label for="login-code">6 位验证码</label>
            <input id="login-code" ref="codeInput" v-model="loginCode" class="code-input" type="text" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" placeholder="000000" required>
            <p v-if="session.authError" class="auth-error" role="alert">{{ session.authError }}</p>
            <button class="primary-button auth-submit" type="submit" :disabled="session.submitting || loginCode.length !== 6">
              {{ session.submitting ? '正在校验…' : '验证并进入' }} <Right />
            </button>
          </form>
          <div class="auth-secondary">
            <button type="button" :disabled="session.submitting" @click="changeEmail">更换邮箱</button>
            <button type="button" :disabled="session.submitting" @click="resendCode">重新发送</button>
          </div>
          <small>验证码 10 分钟内有效、最多尝试 5 次，验证成功后立即作废。</small>
        </template>
      </section>
    </div>
  </div>

  <div v-else class="app-frame">
    <header class="topbar">
      <div class="brand-lockup">
        <span class="brand-seal">{{ productMark }}</span>
        <div>
          <strong>{{ session.bootstrap.productName }}</strong>
          <span>{{ session.bootstrap.mode === 'control' ? 'CONTROL OFFICE' : session.bootstrap.domain }}</span>
        </div>
      </div>
      <div class="status-rail">
        <span class="live-dot" />
        <span>系统在线</span>
        <span class="divider" />
        <span>{{ clock }}</span>
        <span class="identity">{{ session.bootstrap.session.email }}</span>
        <button class="logout-button" type="button" title="退出登录" aria-label="退出登录" @click="logout"><SwitchButton /></button>
      </div>
    </header>

    <main>
      <ControlConsole v-if="session.bootstrap.mode === 'control'" />
      <Mailbox v-else />
    </main>

    <footer class="app-footer">
      <span><Connection /> Cloudflare Edge</span>
      <span><Message /> Mail Center 0.2</span>
      <span><Setting /> {{ session.bootstrap.session.role }}</span>
    </footer>
  </div>
</template>
